import React, { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { useHistory } from "react-router";
import { StatusCode } from "api/protocols";
import { showError, showSuccess } from "redux/actions/ui";

import {
  GetFoodOrderingSettings,
  PutFoodOrderingSettings,
  IFoodOrder,
} from "api/rpc/2024-04/facilityAdmin/facility/foodOrdering";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowUpRightFromSquare } from "@fortawesome/pro-solid-svg-icons";
import { faArrowLeftLong } from "@fortawesome/pro-regular-svg-icons";
import { faBell, faCog } from "@fortawesome/pro-light-svg-icons";

import { NavigationDropdownNew } from "components/navigationDropdownNew/NavigationDropdownNew";
import { QRCode } from "react-qrcode-logo";
import { ButtonNew } from "components/buttonNew";
import Popover from "components/popover/Popover";
import FeatureIcon from "components/icon/FeatureIcon";
import Checkbox from "components/checkbox/Checkbox";
import Toggle from "components/form/toggle/Toggle";

import FoodOrderChit from "./foodOrderChit/FoodOrderChit";
import FoodOrderDetails from "./foodOrderDetails/foodOrderDetails";

import "./foodOrder.scss";
import moment from "moment";
import { concat } from "lodash";
import { useTranslation } from "react-i18next";
import {
  GetKitchenChits,
  TGetKitchenChitRange,
  PutKitchenChit,
} from "api/rpc/2024-04/facilityAdmin/facility/kitchen/chit";

interface IChitStatus {
  id: number;
  msg: string;
  elevated: string;
}

export default function FoodOrderChitMenu() {
  const { facilityStore, authStore } = useAppSelector(store => store);
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const [foodOrders, setFoodOrders] = useState<IFoodOrder[]>([]);
  const [displayedFoodOrders, setDisplayedFoodOrders] = useState<IFoodOrder[]>([]);
  const [showStatusPopover, setShowStatusPopover] = useState(false);
  // const [forceRefresh, setForceRefresh] = useState(false);

  const [foodOrderDetails, setFoodOrderDetails] = useState({
    showFoodOrderDetails: false,
    selectedChit: null,
  });

  const [foodOrderSettings, setFoodOrderSettings] = useState({
    acceptFoodOrders: false,
    members_only: false,
    showFoodOrderBar: false,
    markOrdersAsOvertime: false,
    markOrdersAsOvertimeMinutes: 0,
    sendReadyText: false,
  });

  const [orderCount, setOrderCount] = useState({
    newOrders: 0,
    preparingOrders: 0,
    doneOrders: 0,
    voidOrders: 0,
    archivedOrders: 0,
  });

  const [dropdownOptionsToggle, setDropdownOptionsToggle] = useState({
    showRestrict: true,
    showPaymentSetup: true,
    showPlugIns: true,
    showQRCode: true,
    showColourSettings: true,
  });

  const [filterState, setFilterState] = useState({
    new: false,
    prepared: false,
    done: false,
    overdue: false,
    archived: false,
    void: false,
  });

  // const [prevChitIds, setPrevChitIds] = useState<number[]>([]);

  const [notificationState, setNotificationState] = useState({
    isDropdownOpen: false,
  });


  // Load every 20s
  useEffect(() => {
    if (foodOrderSettings.acceptFoodOrders) {
      // let mounted = true;
      // let interval: undefined | NodeJS.Timeout;

      // clearInterval(interval);
      const interval = setInterval(() => {
        void loadFoodOrders(false);
      }, 20000);

      // if (mounted) {
      //   setForceRefresh(false);
      // }

      return () => {
        clearInterval(interval);
        // mounted = false;
      };
    }
  }, [foodOrders]);

  useEffect(() => {
    void loadFoodOrderSettings();
  }, []);

  useEffect(() => {
    void loadFoodOrders(true);
  }, [foodOrderSettings.acceptFoodOrders]);

  useEffect(() => {
    let newOrders;
    let archivedOrders;
    let doneOrders;
    let overdueOrders;
    let preparedOrders;
    let voidOrders;
    let filterCount = 0;
    let filteredOrders;
    if (filterState.new) {
      newOrders = filterChits("new");
      filterCount++;
    }

    if (filterState.archived) {
      archivedOrders = filterChits("archived");
      filterCount++;
    }

    if (filterState.done) {
      doneOrders = filterChits("done");
      filterCount++;
    }

    if (filterState.overdue) {
      overdueOrders = filterChits("overdue");
      filterCount++;
    }

    if (filterState.prepared) {
      preparedOrders = filterChits("preparing");
      filterCount++;
    }

    if (filterState.void) {
      voidOrders = filterChits("void");
      filterCount++;
    }

    if (!filterCount) {
      filteredOrders = foodOrders;
    } else {
      filteredOrders = concat(newOrders, archivedOrders, doneOrders, overdueOrders, preparedOrders, voidOrders).filter(
        item => item,
      );
    }

    setDisplayedFoodOrders(filteredOrders);
  }, [filterState, foodOrders]);

  function filterChits(status: string) {
    const filteredFoodOrders = foodOrders.map(order => {
      return order.status.toLowerCase() === status ? order : undefined;
    });

    return filteredFoodOrders.filter(item => item);
  }

  async function loadFoodOrderSettings() {
    const getFoodOrderSettingsRes = await GetFoodOrderingSettings({}, true);
    if (getFoodOrderSettingsRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.food_order.food_order_chit_menu.001")));
      return;
    }

    setFoodOrderSettings(prev => ({
      ...prev,
      acceptFoodOrders: getFoodOrderSettingsRes.data.enabled,
      members_only: getFoodOrderSettingsRes.data.members_only,
      markOrdersAsOvertime: getFoodOrderSettingsRes.data.mark_orders_as_overtime,
      markOrdersAsOvertimeMinutes: getFoodOrderSettingsRes.data.mark_orders_as_overtime_after_minutes,
      sendReadyText: getFoodOrderSettingsRes.data.send_ready_text,
    }));
  }

  async function loadFoodOrders(useGlobalLoader: boolean) {
    const before = moment().utc().subtract(12, "hours").format("YYYY-MM-DD HH:mm:ss");
    const after = moment().utc().add(24, "hours").format("YYYY-MM-DD HH:mm:ss");
    const getKitchenChitParams: TGetKitchenChitRange = {
      start_date: before,
      end_date: after,
      foh: true,
    };

    const getChits = await GetKitchenChits(getKitchenChitParams, useGlobalLoader);
    if (getChits.status !== StatusCode.OK) {
      dispatch(showError(String(t("secure.facility.food_order.food_order_chit_menu.002"))));
      return;
    }

    let newCounter = 0;
    let preparingCounter = 0;
    let doneCounter = 0;
    let voidCounter = 0;
    let archivedCounter = 0;

    getChits.data.forEach((order: { status: string }) => {
      if (order.status.toLowerCase() === "new") {
        newCounter += 1;
      } else if (order.status.toLowerCase() === "preparing") {
        preparingCounter++;
      } else if (order.status.toLowerCase() === "done") {
        doneCounter++;
      } else if (order.status.toLowerCase() === "void") {
        voidCounter++;
      } else if (order.status.toLowerCase() === "archived") {
        archivedCounter++;
      }
    });

    setOrderCount(prev => ({
      ...prev,
      newOrders: newCounter,
      preparingOrders: preparingCounter,
      doneOrders: doneCounter,
      voidOrders: voidCounter,
      archivedOrders: archivedCounter,
    }));

    setFoodOrders(getChits.data as IFoodOrder[]);
    setDisplayedFoodOrders(getChits.data as IFoodOrder[]);
  }

  const showacceptFoodOrdersPopover = () => {
    const popoverStatus = showStatusPopover;
    setShowStatusPopover(!popoverStatus);
  };

  function backToDashboard() {
    history.push("/admin/food-ordering/overview");
  }

  // TODO: Add this when Daniel's Order Settings page is merged
  // function navigateToFoodOrderSettingsPage() {
  //   console.log("Show the popup menu");
  //   // setShowacceptFoodOrders(prev => !prev);
  // }

  // const handleRestrictFoodOrders = async () => {
  //   const membersOnly = foodOrderSettings.members_only;
  //   const putFoodOrderSettingsRes = await PutFoodOrderingSettings({ members_only: !membersOnly }, true);
  //   if (putFoodOrderSettingsRes.status !== StatusCode.OK) {
  //     dispatch(showError(t("secure.facility.food_order.food_order_chit_menu.003")));
  //     return;
  //   }

  //   dispatch(showSuccess(t("secure.facility.food_order.food_order_chit_menu.004")));
  //   setFoodOrderSettings(prev => ({ ...prev, members_only: putFoodOrderSettingsRes.data.members_only }));
  // };

  const handleOpenDetails = (e: React.MouseEvent<HTMLDivElement>, order: IFoodOrder) => {
    setFoodOrderDetails(prev => ({ ...prev, showFoodOrderDetails: !prev.showFoodOrderDetails, selectedChit: order }));
  };

  async function handleElevateOrderStatus(
    e: React.MouseEvent<HTMLElement>,
    chitStatus: IChitStatus,
    order: IFoodOrder,
  ) {
    e.stopPropagation();
    if (chitStatus === undefined) {
      dispatch(showError(t("secure.facility.food_order.food_order_chit_menu.005")));
      return;
    }

    const elevatedStatus = chitStatus.elevated;
    const putStatusParams = {
      id: order?.id,
      status: elevatedStatus,
      send_text: foodOrderSettings.sendReadyText,
    };

    const putChitStatusRes = await PutKitchenChit(putStatusParams, true);

    if (putChitStatusRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.food_order.food_order_chit_menu.006")));
      return;
    }

    dispatch(showSuccess(t("secure.facility.food_order.food_order_chit_menu.007")));
    void loadFoodOrders(true);
  }

  async function toggleAcceptFoodOrders() {
    const acceptFoodOrderValue = foodOrderSettings.acceptFoodOrders;
    const putFoodOrderRes = await PutFoodOrderingSettings({ enabled: !acceptFoodOrderValue }, true);
    if (putFoodOrderRes.status !== StatusCode.OK) {
      dispatch(showError(String(t("secure.facility.food_order.food_order_chit_menu.008"))));
      return;
    }

    setShowStatusPopover(prev => !prev);
    setFoodOrderSettings(prev => ({ ...prev, acceptFoodOrders: putFoodOrderRes?.data.enabled }));
  }

  function sendToSettings() {
    console.log("Send to settings");
    history.push("/admin/food-ordering/overview");
  }

  function handleNotificationClick(e: React.MouseEvent) {
    e.stopPropagation();
    setNotificationState(prev => ({ ...prev, isDropdownOpen: !prev.isDropdownOpen }));
  }

  return (
    <>
      {/* Header */}
      <div className="food-order-header">
        {/* Back Btn */}
        <div className="food-order-header-display">
          <div className="pr-8">
            <FontAwesomeIcon icon={faArrowLeftLong} size="lg" fixedWidth onClick={backToDashboard} />
          </div>

          {/* Food Order Status */}
          {/* Toggle Food Status */}
          <div className="food-order-status-display" onClick={showacceptFoodOrdersPopover}>
            {foodOrderSettings.acceptFoodOrders ? (
              <div className="food-order-status-on"></div>
            ) : (
              <div className="food-order-status-off"></div>
            )}
            <div></div>

            {t("secure.facility.food_order.food_order_chit_menu.009")}
            {foodOrderSettings.acceptFoodOrders
              ? t("secure.facility.food_order.food_order_chit_menu.010")
              : t("secure.facility.food_order.food_order_chit_menu.011")}
          </div>

          <div className="food-order-popover-container">
            <Popover
              active={showStatusPopover}
              handleClosePopover={() => {
                setShowStatusPopover(false);
              }}
            >
              <div className="food-order-status-container">
                <Toggle
                  labelRight={t("secure.facility.food_order.food_order_chit_menu.012")}
                  onChange={() => toggleAcceptFoodOrders()}
                  checked={foodOrderSettings.acceptFoodOrders}
                />

                {t("secure.facility.food_order.food_order_chit_menu.013")}
              </div>
            </Popover>
          </div>
        </div>

        {/* Title */}
        <h1>{facilityStore?.facility?.long_name}</h1>

        {/* Settings */}
        <div className="food-order-header-settings">
          {/* Setting Cog */}
          <div className="cog-container align-center">
            <NavigationDropdownNew
              rightAlign
              showPlainTextLabel
              persist={true}
              label={<FontAwesomeIcon icon={faCog} size="lg" fixedWidth />}
              sections={[
                [
                  // Settings
                  // {
                  //   label: t("secure.facility.food_order.food_order_chit_menu.014"),
                  //   icon: dropdownOptionsToggle.showRestrict ? "angle-down" : "angle-up",
                  //   onClick: () => setDropdownOptionsToggle(prev => ({ ...prev, showRestrict: !prev.showRestrict })),
                  // },
                  // Restrict Food Orders
                  // {
                  //   label: t("secure.facility.food_order.food_order_chit_menu.015"),
                  //   icon: foodOrderSettings.members_only ? "square-check" : "square",
                  //   hidden: dropdownOptionsToggle.showRestrict,
                  //   onClick: () => handleRestrictFoodOrders(),
                  // },
                  // QR Code
                  // TODO: Add this when Daniel's Order Settings page is merged
                  {
                    label: t("secure.facility.food_order.food_order_chit_menu.016"),
                    icon: dropdownOptionsToggle.showQRCode ? "angle-down" : "angle-up",
                    onClick: () => setDropdownOptionsToggle(prev => ({ ...prev, showQRCode: !prev.showQRCode })),
                  },
                  {
                    label: (
                      <div className="food-order-settings-qr">
                        <QRCode value={`${window.origin}/portal/golfnorth/food-ordering/menu/${facilityStore.facility?.short_name}`} size={90} qrStyle="fluid" ecLevel="H" />
                      </div>
                    ),
                    hidden: dropdownOptionsToggle.showQRCode,
                    onClick: () => console.log("QR Shown"),
                  },
                ],
              ]}
            />
          </div>

          {/* Notifications */}
          <div
            className="fa-stack fa-layers fa-fw fa-lg mr-2 justify-center food-order-notification-icon-container"
            data-count={String(orderCount.newOrders)}
          >
            <FontAwesomeIcon icon={faBell} fixedWidth onClick={handleNotificationClick} className="cursor-pointer"/>

            <div
              style={{
                display: notificationState.isDropdownOpen && orderCount.newOrders > 0 ? "block" : "none",
                right: 0,
                left: null,
                top: "100%",
              }}
              className="notification-new-content"
            >
              <div className="notification-new-content-section">
                {displayedFoodOrders
                  ?.filter(order => order.status === "new")
                  .map((order, orderIndex) => {
                    console.log(order);
                    return (
                      <div key={orderIndex} className="notification-new-content-section-option">
                        <p>Order: {order?.order?.name}</p>
                        
                        <p>Items: {order?.line_items?.length}</p>
                        
                      </div>
                    );
                  })}
              </div>
            </div>
          </div>

          {/* </span> */}

          {/* Avatar */}
          <div className="food-order-user-icon-container">
            <div className="food-order-user-icon-inner">
              {authStore?.user?.first_name?.charAt(0)}
              {authStore?.user?.last_name?.charAt(0)}
            </div>
          </div>
        </div>
      </div>

      {/* Order Status Bar */}
      {foodOrderSettings.acceptFoodOrders && foodOrders.length >= 1 ? (
        <div className="food-order-status-bar">
          {/* Total #, New Orders, Prepping, Done, Late */}
          <span className="food-order-status-bar-filter">
            <Checkbox onChange={() => setFilterState(prev => ({ ...prev, void: !prev.void }))} />
            <span className="food-order-status-bar-void"></span>
            {t("secure.facility.food_order.food_order_chit_menu.017")}
            <span className="food-order-status-bar-filter-quantity">{orderCount.voidOrders}</span>
          </span>

          <span className="food-order-status-bar-filter">
            <Checkbox onChange={() => setFilterState(prev => ({ ...prev, archived: !prev.archived }))} />
            <span className="food-order-status-bar-archived"></span>
            {t("secure.facility.food_order.food_order_chit_menu.018")}
            <span className="food-order-status-bar-filter-quantity">{orderCount.archivedOrders}</span>
          </span>

          <span className="food-order-status-bar-filter">
            <Checkbox onChange={() => setFilterState(prev => ({ ...prev, done: !prev.done }))} />
            <span className="food-order-status-bar-done"></span>
            {t("secure.facility.food_order.food_order_chit_menu.019")}
            <span className="food-order-status-bar-filter-quantity">{orderCount.doneOrders}</span>
          </span>

          <span className="food-order-status-bar-filter">
            <Checkbox onChange={() => setFilterState(prev => ({ ...prev, prepared: !prev.prepared }))} />
            <span className="food-order-status-bar-preparing"></span>
            {t("secure.facility.food_order.food_order_chit_menu.020")}
            <span className="food-order-status-bar-filter-quantity">{orderCount.preparingOrders}</span>
          </span>

          <span className="food-order-status-bar-filter">
            <Checkbox onChange={() => setFilterState(prev => ({ ...prev, new: !prev.new }))} />
            <span className="food-order-status-bar-new"></span>
            {t("secure.facility.food_order.food_order_chit_menu.021")}
            <span className="food-order-status-bar-filter-quantity">{orderCount.newOrders}</span>
          </span>
        </div>
      ) : null}

      {foodOrderSettings.acceptFoodOrders ? (
        <>
          {/* Chits */}
          {foodOrders.length >= 1 ? (
            <div className="food-order-container">
              <div className="food-order-chits-container">
                {displayedFoodOrders
                  ?.sort((a, b) => a.id - b.id)
                  ?.map((order, orderIndex) => {
                    return order?.status === "archived" && !filterState.archived ? null : (
                      <FoodOrderChit
                        key={orderIndex}
                        order={order}
                        onClick={e => handleOpenDetails(e, order)}
                        elevateStatus={(e, elevatedStatus, order) => handleElevateOrderStatus(e, elevatedStatus, order)}
                        markAsOvertime={foodOrderSettings.markOrdersAsOvertime}
                        markAsOvertimeMinutes={foodOrderSettings.markOrdersAsOvertimeMinutes}
                      />
                    );
                  })}
              </div>
            </div>
          ) : (
            <div className="food-order-body-warning">
              <FeatureIcon type="warning" theme="light-circle-outline" size="large" icon="exclamation-circle" />
              <p className="center-text">{t("secure.facility.food_order.food_order_chit_menu.026")}</p>
              <p className="center-text">{t("secure.facility.food_order.food_order_chit_menu.027")}</p>

              {/* Routes to settings page on MAIN page, not Food Ordering */}
              <ButtonNew type="link-color" className="pl-3 pt-5" onClick={sendToSettings}>
                {t("secure.facility.food_order.food_order_chit_menu.022")}{" "}
                <FontAwesomeIcon icon={faArrowUpRightFromSquare} size="2x" fixedWidth />{" "}
              </ButtonNew>
            </div>
          )}
        </>
      ) : (
        // Food Orders Off Warning
        <div className="food-order-body-warning">
          <FeatureIcon type="warning" theme="light-circle-outline" size="large" icon="exclamation-circle" />

          <p className="center-text">{t("secure.facility.food_order.food_order_chit_menu.023")}</p>

          <p className="center-text">{t("secure.facility.food_order.food_order_chit_menu.025")}</p>

          {/* Routes to settings page on MAIN page, not Food Ordering */}
          <ButtonNew type="link-color" className="pl-3 pt-5" onClick={sendToSettings}>
            {t("secure.facility.food_order.food_order_chit_menu.024")}{" "}
            <FontAwesomeIcon icon={faArrowUpRightFromSquare} size="2x" fixedWidth />{" "}
          </ButtonNew>
        </div>
      )}

      <FoodOrderDetails
        isActive={foodOrderDetails.showFoodOrderDetails}
        order={foodOrderDetails.selectedChit}
        onCancel={() => setFoodOrderDetails(prev => ({ ...prev, showFoodOrderDetails: !prev.showFoodOrderDetails }))}
      />
    </>
  );
}
